import { useCallback, useEffect, useState } from "react";
import { Home, Printer } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { Checkbox } from "@/components/ui/checkbox";

const FIELDS = [["wifi_name", "Wi-Fi name", 60], ["wifi_password", "Wi-Fi password", 60], ["doors", "Doors and garage", 500], ["alarm", "Alarm instructions", 500],
  ["trash", "Trash and recycling day", 200], ["rules", "House rules", 1000], ["where", "Where to find things", 1000]];
const MORE = [["trash_schedule", "Trash & recycling schedule"], ["thermostat", "Thermostat & quirks"], ["appliances", "Appliances & quirks"], ["parking", "Parking"], ["mail", "Mail & packages"], ["garden", "Garden & plants"]];
export const HOUSE_LABELS = Object.fromEntries([...FIELDS, ...MORE].map(([k, l]) => [k, l]));

export function PrintCard({ hubId, members, onClose }) {
  const [keys, setKeys] = useState(members.map((m) => m.key));
  const [paper, setPaper] = useState("letter");
  const [busy, setBusy] = useState(false);
  const make = async (download) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/organizations/${hubId}/emergency/card.pdf`, { params: { keys: keys.join(","), paper }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (download) { const a = document.createElement("a"); a.href = url; a.download = "emergency-card.pdf"; a.click(); } else window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title="Print card" description="One page for the fridge. Never includes Documents." testid="print-card-dialog">
      <p className="q-label mb-1">People to include</p>
      {members.map((m) => (
        <label key={m.key} className="flex min-h-[44px] items-center gap-3 text-sm text-q-text">
          <Checkbox className="h-6 w-6" checked={keys.includes(m.key)} onCheckedChange={(v) => setKeys((x) => (v ? [...x, m.key] : x.filter((k) => k !== m.key)))} data-testid={`print-pick-${m.name.split(" ")[0]}`} /> {m.name}
        </label>
      ))}
      <Field label="Paper"><select className="q-input min-h-[44px] w-full" value={paper} onChange={(e) => setPaper(e.target.value)} data-testid="print-paper"><option value="letter">Letter</option><option value="a4">A4</option></select></Field>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button className="q-btn q-btn-secondary min-h-[44px]" disabled={busy || !keys.length} onClick={() => make(true)} data-testid="print-download-btn">Download PDF</button>
        <button className="q-btn q-btn-primary min-h-[44px]" disabled={busy || !keys.length} onClick={() => make(false)} data-testid="print-open-btn"><Printer className="h-4 w-4" /> Open to print</button>
      </div>
    </FormDialog>
  );
}

function HouseEditor({ hubId, info, warning, onClose, onSaved }) {
  const [f, setF] = useState(info);
  const [busy, setBusy] = useState(false);
  const save = () => { setBusy(true); api.put(`/organizations/${hubId}/emergency/house-info`, f).then(() => { toast.success("House info saved"); onSaved(); onClose(); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false)); };
  return (
    <FormDialog open onClose={onClose} title="House info for guests" description="Shown only in Guest passes that include it, and to parents." testid="house-editor">
      <p className="mb-3 rounded-xl border p-3 text-sm font-semibold" style={{ borderColor: "rgba(var(--glow-lists),0.5)", color: "rgb(var(--glow-lists))" }} data-testid="house-warning">{warning}</p>
      <div className="space-y-3">
        {FIELDS.map(([k, l, n]) => (
          <Field key={k} label={`${l} (optional)`}>
            {n > 100 ? <textarea className="q-input min-h-[64px] w-full" maxLength={n} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`house-${k}`} />
              : <input className="q-input min-h-[44px] w-full" maxLength={n} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`house-${k}`} />}
          </Field>
        ))}
      </div>
      <button className="q-btn q-btn-primary mt-4 min-h-[44px] w-full" disabled={busy} onClick={save} data-testid="house-save-btn">Save</button>
    </FormDialog>
  );
}

export function HouseBody({ info }) {
  const rows = Object.keys(info || {}).filter((k) => info[k]).map((k) => [k, HOUSE_LABELS[k] || k]);
  if (!rows.length) return <p className="text-sm text-q-muted" data-testid="house-empty">Nothing added yet.</p>;
  return <dl className="space-y-2">{rows.map(([k, l]) => <div key={k} data-testid={`house-row-${k}`}><dt className="text-[10px] font-black uppercase tracking-[0.14em] text-q-faint">{l}</dt><dd className="whitespace-pre-wrap break-words text-sm text-q-text">{info[k]}</dd></div>)}</dl>;
}

export default function HouseInfo({ hubId, readOnly }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => api.get(`/organizations/${hubId}/emergency/house-info`).then(({ data }) => setD(data)).catch(() => {}), [hubId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  return (
    <section className="q-card mt-6" data-testid="house-info">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Home className="h-5 w-5 text-[rgb(var(--glow-family))]" />
        <h2 className="flex-1 font-display text-lg font-extrabold text-q-text">House info for guests</h2>
        <Link to={`/h/${hubId}/manual`} className="q-btn q-btn-secondary min-h-[44px]" data-testid="house-open-manual">Open Household manual</Link>
        <button className="q-btn q-btn-ghost min-h-[44px]" disabled={readOnly} onClick={() => setEdit(true)} data-testid="house-edit-btn">Edit</button>
      </div>
      <HouseBody info={d.house_info} />
      {edit && <HouseEditor hubId={hubId} info={d.house_info} warning={d.warning} onClose={() => setEdit(false)} onSaved={load} />}
    </section>
  );
}
